import { useState } from 'react';
import { Button, Table, Form, Modal, Row, Col, Alert, Card, Badge, Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    useGetCategoriesQuery,
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
} from '../../slices/categoryApiSlice';
import { useDeleteImageMutation } from '../../slices/utilsApiSlice';
import { compressAndUploadImage } from '../../utils/compressAndUploadImage';
import ImageCropper from '../../components/ui/ImageCropper';
import Loader from '../../components/layout/Loader';
import Meta from '../../components/layout/Meta';
import { FaPencil } from 'react-icons/fa6';
import { FaTrash, FaFolder, FaPlus } from 'react-icons/fa';
import useIsMobile from '../../utils/useIsMobile';

const CategoryListScreen = () => {
    const isMobile = useIsMobile();
    const { data: categories, isLoading, error } = useGetCategoriesQuery();
    const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
    const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();
    const [deleteCategory, { isLoading: isDeleting }] = useDeleteCategoryMutation();
    const [deleteImage] = useDeleteImageMutation();

    const [modalShow, setModalShow] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [showCropper, setShowCropper] = useState(false);
    const [rawImageSrc, setRawImageSrc] = useState(null);
    const [formData, setFormData] = useState({
        _id: '',
        name: '',
        description: '',
        image: '',
        parentCategoryId: '',
    });

    const handleOpen = (cat = {}) => {
        setEditMode(!!cat._id);
        setFormData({
            _id: cat._id || '',
            name: cat.name || '',
            description: cat.description || '',
            image: cat.image || '',
            parentCategoryId: cat.parentCategoryId?._id || cat.parentCategoryId || '',
        });
        setModalShow(true);
    };

    const handleClose = () => {
        setModalShow(false);
        setFormData({ _id: '', name: '', description: '', image: '', parentCategoryId: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editMode) {
                await updateCategory(formData).unwrap();
            } else {
                await createCategory(formData).unwrap();
            }
            handleClose();
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar la categoría?')) {
            try {
                await deleteCategory(id).unwrap();
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            setRawImageSrc(reader.result);
            setShowCropper(true);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleCropComplete = async (croppedImageFile) => {
        const previousImage = formData.image;
        setUploading(true);

        try {
            const response = await compressAndUploadImage(croppedImageFile, 'image');
            const urls =
                response.images || (Array.isArray(response.image) ? response.image : [response.image]);
            const nextImage = urls[0];

            if (!nextImage) {
                toast.error('No se recibió la URL de la imagen subida');
                return;
            }

            if (previousImage && previousImage !== nextImage) {
                await deleteImage({ imageName: previousImage }).unwrap();
            }

            setFormData((prevData) => ({
                ...prevData,
                image: nextImage,
            }));
            toast.success(response.message || 'Imagen subida correctamente');
        } catch (err) {
            toast.error(err?.data?.message || err.error || err.message || 'Error al subir imagen');
        } finally {
            setUploading(false);
            setShowCropper(false);
            setRawImageSrc(null);
        }
    };

    return (
        <Container className="py-4">
            <Meta title="Lista de Categorías" />
            <div className="admin-header mb-4">
                <h1 className="admin-title responsive-title d-flex align-items-center gap-3">
                    <div className="p-2 rounded-circle bg-light text-primary">
                        <FaFolder size={32} />
                    </div>
                    <div>
                        Categorías
                        {categories?.length > 0 && (
                            <Badge bg="secondary" className="ms-2 fs-6 align-middle">
                                {categories.length}
                            </Badge>
                        )}
                    </div>
                </h1>
                <Button 
                    variant="primary" 
                    onClick={() => handleOpen()} 
                    className="d-flex align-items-center gap-2 shadow-sm"
                    style={{ padding: '0.6rem 1.2rem', borderRadius: '8px' }}
                >
                    <FaPlus /> Nueva Categoría
                </Button>
            </div>

            {isLoading || isCreating || isUpdating || isDeleting ? (
                <Loader />
            ) : error ? (
                <Alert variant="danger">{error?.data?.message || error.error}</Alert>
            ) : (
                <>
                    {!isMobile && (
                        <div className="admin-table-container shadow-sm mb-4 bg-white rounded-4 overflow-hidden border">
                            <Table hover responsive className="admin-table m-0 align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="py-3 ps-4 text-secondary text-uppercase small fw-bold border-0">Nombre</th>
                                        <th className="py-3 text-secondary text-uppercase small fw-bold border-0">Descripción</th>
                                        <th className="py-3 text-secondary text-uppercase small fw-bold border-0">Imagen</th>
                                        <th className="py-3 text-secondary text-uppercase small fw-bold border-0">Categoría Padre</th>
                                        <th className="py-3 pe-4 text-center text-secondary text-uppercase small fw-bold border-0">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.map((cat) => (
                                        <tr key={cat._id} className="border-bottom">
                                            <td className="ps-4 fw-semibold text-dark">{cat.name}</td>
                                            <td className="text-muted small" style={{ maxWidth: '300px' }}>
                                                <div className="text-truncate">{cat.description}</div>
                                            </td>
                                            <td>
                                                {cat.image ? (
                                                    <div className="rounded overflow-hidden border" style={{ width: '48px', height: '48px' }}>
                                                        <img
                                                            src={cat.image}
                                                            alt={cat.name}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="rounded bg-light d-flex align-items-center justify-content-center text-muted border" style={{ width: '48px', height: '48px' }}>
                                                        <FaFolder />
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {cat.parentCategoryId ? (
                                                    <Badge bg="light" text="dark" className="border fw-normal px-3 py-2">
                                                        {cat.parentCategoryId.name || 'Subcategoría'}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-muted small fst-italic">-</span>
                                                )}
                                            </td>
                                            <td className="pe-4">
                                                <div className="action-buttons d-flex justify-content-center gap-2">
                                                    <Button
                                                        variant="light"
                                                        className="btn-icon text-primary border-0 shadow-sm"
                                                        onClick={() => handleOpen(cat)}
                                                        title="Editar"
                                                        style={{ width: '36px', height: '36px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    >
                                                        <FaPencil size={14} />
                                                    </Button>
                                                    <Button
                                                        variant="light"
                                                        className="btn-icon text-danger border-0 shadow-sm"
                                                        onClick={() => handleDelete(cat._id)}
                                                        title="Eliminar"
                                                        style={{ width: '36px', height: '36px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    >
                                                        <FaTrash size={14} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {isMobile && (
                        <div className="category-cards">
                            {categories.map((cat) => (
                                <Card key={cat._id} className="mb-3 shadow-sm border-0 rounded-4 overflow-hidden">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center mb-3">
                                            {cat.image ? (
                                                <img
                                                    src={cat.image}
                                                    alt={cat.name}
                                                    className="rounded-3 me-3 border"
                                                    style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div 
                                                    className="rounded-3 me-3 bg-light d-flex align-items-center justify-content-center border"
                                                    style={{ width: '64px', height: '64px' }}
                                                >
                                                    <FaFolder size={24} className="text-muted" />
                                                </div>
                                            )}
                                            <div className="flex-grow-1">
                                                <h5 className="mb-1 fw-bold text-dark">{cat.name}</h5>
                                                {cat.parentCategoryId && (
                                                    <Badge bg="light" text="dark" className="border fw-normal">
                                                        Padre: {cat.parentCategoryId.name}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-muted small mb-3 bg-light p-2 rounded-3">
                                            {cat.description || 'Sin descripción'}
                                        </p>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="outline-primary"
                                                className="flex-grow-1 d-flex align-items-center justify-content-center gap-2 py-2 rounded-3"
                                                onClick={() => handleOpen(cat)}
                                            >
                                                <FaPencil /> Editar
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                className="flex-grow-1 d-flex align-items-center justify-content-center gap-2 py-2 rounded-3"
                                                onClick={() => handleDelete(cat._id)}
                                            >
                                                <FaTrash /> Eliminar
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}
                </>
            )}

            <Modal show={modalShow} onHide={handleClose} centered size="lg" contentClassName="border-0 rounded-4 shadow-lg">
                <Modal.Header closeButton className="border-bottom-0 pb-0">
                    <Modal.Title className="fw-bold text-primary">
                        {editMode ? 'Editar Categoría' : 'Crear Categoría'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="pt-4">
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label column='sm' className="fw-semibold small text-muted text-uppercase">Nombre</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Nombre de la categoría"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        className="form-control-lg fs-6"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label column='sm' className="fw-semibold small text-muted text-uppercase">Categoría Padre</Form.Label>
                                    <Form.Select
                                        value={formData.parentCategoryId}
                                        onChange={(e) => setFormData({ ...formData, parentCategoryId: e.target.value })}
                                        className="form-select-lg fs-6"
                                    >
                                        <option value="">Ninguna (Categoría Principal)</option>
                                        {categories && categories
                                            .filter(c => c._id !== formData._id) // Prevent self-parenting
                                            .map((cat) => (
                                                <option key={cat._id} value={cat._id}>
                                                    {cat.name}
                                                </option>
                                            ))
                                        }
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label column='sm' className="fw-semibold small text-muted text-uppercase">Imagen</Form.Label>
                                    <div className="d-flex gap-3 align-items-start">
                                        <div className="flex-grow-1">
                                            <Form.Control
                                                type="file"
                                                onChange={handleImageUpload}
                                                accept="image/*"
                                                className="mb-2"
                                            />
                                            <Form.Text className="text-muted">
                                                Formatos: JPG, PNG, WEBP. Máx 2MB.
                                            </Form.Text>
                                        </div>
                                        {formData.image && (
                                            <div className="border rounded p-1 bg-light flex-shrink-0" style={{ width: '80px', height: '80px' }}>
                                                <img
                                                    src={formData.image}
                                                    alt="Vista previa"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    {uploading && <Loader />}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label column='sm' className="fw-semibold small text-muted text-uppercase">Descripción</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Descripción detallada de la categoría..."
                                value={formData.description}
                                onChange={(e) =>
                                    setFormData({ ...formData, description: e.target.value })
                                }
                                required
                                className="fs-6"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-top-0 pt-0 pb-4 px-4">
                        <Button variant="light" onClick={handleClose} className="px-4 rounded-3">
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={isCreating || isUpdating} className="px-4 rounded-3 shadow-sm">
                            {editMode ? 'Actualizar' : 'Crear'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
            <Modal
                show={showCropper}
                onHide={() => {
                    setShowCropper(false);
                    setRawImageSrc(null);
                }}
                size="lg"
                centered
                fullscreen="sm-down"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Recortar Imagen</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {rawImageSrc && (
                        <ImageCropper
                            imageSrc={rawImageSrc}
                            onCropComplete={handleCropComplete}
                            onCancel={() => {
                                setShowCropper(false);
                                setRawImageSrc(null);
                            }}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default CategoryListScreen;
